import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * Límite de error en la raíz.
 *
 * La v1 no tenía ninguno: una excepción durante el render —un campo que llegó
 * null, un `id` que no existe en el catálogo— desmontaba el árbol entero y
 * dejaba la ventana en blanco, sin mensaje y sin forma de volver. En una faena
 * eso se lee como "la plataforma se cayó".
 *
 * Sigue siendo una clase porque React no expone los ganchos de error a los
 * componentes de función: `getDerivedStateFromError` y `componentDidCatch` no
 * tienen equivalente en hooks.
 */
interface Estado {
  error: Error | null
}

export class ErrorBoundary extends Component<{ children: ReactNode }, Estado> {
  override state: Estado = { error: null }

  static getDerivedStateFromError(error: Error): Estado {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Al log del navegador con el árbol de componentes: es lo que hace falta
    // para reproducirlo, y es lo que se pide en un reporte.
    console.error('Error de render no controlado:', error, info.componentStack)
  }

  override render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="min-h-screen grid place-items-center bg-acero-100 px-4">
        <div className="w-full max-w-[460px] panel p-5">
          <h1 className="text-[17px] font-semibold text-acero-900">
            Algo se rompió en esta pantalla
          </h1>
          <p className="text-[12.5px] text-acero-600 mt-1.5 leading-relaxed">
            No es un problema de sus datos: la orden, el carrito y el catálogo están intactos en el
            servidor. Recargue para volver a intentarlo.
          </p>

          {/*
            El mensaje técnico va plegado. Quien reporta el problema lo necesita;
            quien solo quiere seguir trabajando no debería tropezarse con él.
          */}
          <details className="mt-3">
            <summary className="text-[11.5px] text-acero-500 cursor-pointer select-none">
              Detalle técnico
            </summary>
            <pre className="num mt-2 text-[11px] text-acero-700 bg-acero-50 border border-acero-200 rounded-sm p-2 whitespace-pre-wrap break-words">
              {error.message}
            </pre>
          </details>

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              className="btn btn-accion"
              onClick={() => window.location.reload()}
            >
              Recargar
            </button>
            {/*
              Enlace nativo y no <Link>: el enrutador puede ser justamente lo
              que está roto, así que la salida no puede depender de él.
            */}
            <a href="/" className="btn btn-secundario">
              Ir al inicio
            </a>
          </div>
        </div>
      </div>
    )
  }
}
